import { useState, useEffect, useRef } from 'react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import './CalendarPicker.css'

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const WEEKDAYS_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const WEEKDAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

const formatHeaderDate = (date) => {
  if (!date) return null
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function CalendarPicker({ startDate, endDate, onDateChange, minDate = new Date(), mode = 'scroll', onClose, singleDate = false }) {
  const scrollRef = useRef(null)
  const [currentMonthOffset, setCurrentMonthOffset] = useState(0)
  
  // Generate 12 months starting from current month
  const generateMonths = () => {
    const months = []
    const today = new Date()
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1)
      months.push(date)
    }
    return months
  }
  
  const months = generateMonths()
  
  // Reset scroll to top when component mounts
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0
    }
  }, [])
  
  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startDayOfWeek = firstDay.getDay()
    
    const days = []
    
    // Add empty cells for days before the 1st
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ day: null, date: null })
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(year, month, day)
      days.push({ day, date: dayDate })
    }
    
    return days
  }
  
  const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate()
  }
  
  const isInRange = (date) => {
    if (!startDate || !endDate || !date) return false
    const time = date.getTime()
    return time > startDate.getTime() && time < endDate.getTime()
  }
  
  const isDisabled = (date) => {
    if (!date) return true
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }
  
  const handleDayClick = (date) => {
    if (!date || isDisabled(date)) return

    if (singleDate) {
      onDateChange(date, null)
      return
    }

    if (!startDate || (startDate && endDate)) {
      // Start new selection
      onDateChange(date, null)
    } else if (startDate && !endDate) {
      // Complete the range
      if (date < startDate) {
        onDateChange(date, startDate)
      } else {
        onDateChange(startDate, date)
      }
    }
  }
  
  const getDayClass = (date) => {
    if (!date) return 'calendar-day empty'

    const classes = ['calendar-day']

    if (isDisabled(date)) {
      classes.push('disabled')
    }

    const today = new Date()
    if (isSameDay(date, today)) {
      classes.push('today')
    }

    const isStart = isSameDay(date, startDate)
    const isEnd = isSameDay(date, endDate)
    const inRange = isInRange(date)
    
    if (singleDate) {
      if (isStart) classes.push('selected-single')
    } else if (isStart && isEnd) {
      classes.push('selected-single')
    } else if (isStart) {
      classes.push('selected-start')
    } else if (isEnd) {
      classes.push('selected-end')
    } else if (inRange) {
      classes.push('in-range')
    }
    
    return classes.join(' ')
  }

  // Side-by-side mode for desktop
  if (mode === 'side-by-side') {
    const month1 = months[currentMonthOffset]
    const month2 = months[currentMonthOffset + 1]
    const canGoBack = currentMonthOffset > 0
    const canGoForward = currentMonthOffset < months.length - 2

    return (
      <div className="calendar-picker side-by-side">
        <div className="calendar-header-row">
          <div className="calendar-header-summary">
            {singleDate ? (
              <div className="calendar-header-col">
                <span className="calendar-header-label">Date</span>
                <span className="calendar-header-value">
                  {formatHeaderDate(startDate) || 'Select date'}
                </span>
              </div>
            ) : (
              <>
                <div className="calendar-header-col">
                  <span className="calendar-header-label">Check-in</span>
                  <span className="calendar-header-value">
                    {formatHeaderDate(startDate) || 'Select date'}
                  </span>
                </div>
                <div className="calendar-header-arrow">→</div>
                <div className="calendar-header-col">
                  <span className="calendar-header-label">Check-out</span>
                  <span className="calendar-header-value">
                    {formatHeaderDate(endDate) || 'Select date'}
                  </span>
                </div>
              </>
            )}
          </div>
          <div className="calendar-nav-btns">
            <button
              type="button"
              className={`calendar-nav-btn ${!canGoBack ? 'disabled' : ''}`}
              onClick={() => canGoBack && setCurrentMonthOffset(currentMonthOffset - 1)}
              disabled={!canGoBack}
              aria-label="Previous month"
            >
              <FiChevronLeft size={18} />
            </button>
            <button
              type="button"
              className={`calendar-nav-btn ${!canGoForward ? 'disabled' : ''}`}
              onClick={() => canGoForward && setCurrentMonthOffset(currentMonthOffset + 1)}
              disabled={!canGoForward}
              aria-label="Next month"
            >
              <FiChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="calendar-grid-container">
          {[month1, month2].map((month, idx) => (
            <div key={idx} className="calendar-grid">
              <div className="month-title">{formatMonthYear(month)}</div>
              <div className="calendar-weekdays-row">
                {WEEKDAYS_SHORT.map((day, i) => (
                  <div key={i} className="weekday-cell">{day}</div>
                ))}
              </div>
              <div className="month-days-grid">
                {getDaysInMonth(month).map((dayObj, dayIdx) => (
                  <div
                    key={dayIdx}
                    className={getDayClass(dayObj.date)}
                    onClick={() => handleDayClick(dayObj.date)}
                  >
                    {dayObj.day && (
                      <>
                        <div className="day-bg"></div>
                        <span className="day-number">{dayObj.day}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="calendar-footer">
          <button
            type="button"
            className="calendar-clear-btn"
            onClick={() => onDateChange(null, null)}
          >
            Clear dates
          </button>
          {onClose && (
            <button
              type="button"
              className="calendar-done-btn"
              onClick={() => onClose()}
            >
              Done
            </button>
          )}
        </div>
      </div>
    )
  }

  // Default scroll mode for mobile
  return (
    <div className="calendar-picker" ref={scrollRef}>
      <div className="calendar-weekdays">
        {WEEKDAYS.map((day, i) => (
          <div key={i} className="weekday">{day}</div>
        ))}
      </div>

      <div className="calendar-months">
        {months.map((month, monthIdx) => (
          <div key={monthIdx} className="calendar-month">
            <div className="month-name">{formatMonthYear(month)}</div>
            <div className="month-days">
              {getDaysInMonth(month).map((dayObj, dayIdx) => (
                <div
                  key={dayIdx}
                  className={getDayClass(dayObj.date)}
                  onClick={() => handleDayClick(dayObj.date)}
                >
                  {dayObj.day && (
                    <>
                      <div className="day-bg"></div>
                      <span className="day-number">{dayObj.day}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="calendar-footer">
        <button
          type="button"
          className="calendar-clear-btn"
          onClick={() => onDateChange(null, null)}
        >
          Clear dates
        </button>
        {onClose && (
          <button
            type="button"
            className="calendar-done-btn"
            onClick={() => onClose()}
          >
            Done
          </button>
        )}
      </div>
    </div>
  )
}

export default CalendarPicker
